import { FunctionsHttpError, SupabaseClient } from '@supabase/supabase-js';

/**
 * Errore restituito da una edge function con corpo JSON.
 * `code` è il codice stabile emesso dal backend (es. 'account_already_linked'):
 * la UI lo usa per scegliere un messaggio tradotto, mentre `message` resta il
 * testo tecnico da mostrare solo come fallback o in console.
 */
export class EdgeFunctionError extends Error {
  readonly code?: string;
  readonly status?: number;
  readonly details?: unknown;

  constructor(message: string, options: { code?: string; status?: number; details?: unknown } = {}) {
    super(message);
    this.name = 'EdgeFunctionError';
    this.code = options.code;
    this.status = options.status;
    this.details = options.details;
  }
}

export async function invokeEdgeFunction<T = unknown>(
  supabase: SupabaseClient,
  functionName: string,
  options?: Parameters<SupabaseClient['functions']['invoke']>[1]
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, options);

  if (error) {
    if (error instanceof FunctionsHttpError) {
      // Su risposta non-2xx supabase-js azzera `data`: il corpo con codice e
      // messaggio è leggibile solo dalla Response in error.context.
      let body: { error?: string; code?: string; details?: unknown } = {};
      try {
        body = await error.context.json();
      } catch {
        // corpo non JSON: resta il messaggio generico di supabase-js
      }
      throw new EdgeFunctionError(body.error ?? error.message, {
        code: body.code,
        status: error.context?.status,
        details: body.details,
      });
    }
    throw new Error(typeof error === 'string' ? error : (error as Error).message);
  }

  return data as T;
}
